import React from 'react';
import './Ts3TraversalPanel.css';

const LEVELS = [
  { key: 'task', label: 'Task' },
  { key: 'stage', label: 'Stage' },
  { key: 'strategy', label: 'Strategy' },
  { key: 'skill', label: 'Skill' },
];

function formatLabel(value) {
  return String(value || 'unknown')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function makeTreeNode(level, value, entry, index) {
  return {
    level,
    value,
    lastTurn: entry.turn,
    lastVisibleTurn: null,
    firstIndex: index,
    children: [],
    active: false,
    faded: false,
  };
}

function markFadedNodes(nodes, visibleCount) {
  nodes.forEach((node) => {
    node.faded = node.firstIndex >= visibleCount;
    markFadedNodes(node.children, visibleCount);
  });
}

function buildTraversalTree(items, visibleCount) {
  const root = { children: [] };
  let currentPath = [];
  let activePath = [];

  items.forEach((entry, index) => {
    const values = LEVELS.map((level) => entry?.[level.key] || '');
    let reuseDepth = 0;

    while (
      reuseDepth < LEVELS.length - 1 &&
      currentPath[reuseDepth] &&
      values[reuseDepth] &&
      currentPath[reuseDepth].value === values[reuseDepth]
    ) {
      reuseDepth += 1;
    }

    currentPath = currentPath.slice(0, reuseDepth);
    let parent = reuseDepth ? currentPath[reuseDepth - 1] : root;

    for (let depth = reuseDepth; depth < LEVELS.length; depth += 1) {
      const level = LEVELS[depth];
      const value = values[depth];
      if (!value) break;

      const node = makeTreeNode(level.key, value, entry, index);
      parent.children.push(node);
      currentPath[depth] = node;
      parent = node;
    }

    currentPath.forEach((node) => {
      node.lastTurn = entry.turn;
    });
    if (index < visibleCount) {
      currentPath.forEach((node) => {
        node.lastVisibleTurn = entry.turn;
      });
      activePath = currentPath.slice();
    }
  });

  activePath.forEach((node) => {
    node.active = true;
  });
  markFadedNodes(root.children, visibleCount);

  return root.children;
}

function TraversalNode({ node, depth, onSelectEntry, onHoverEntry }) {
  const level = LEVELS[depth] || { key: node.level, label: node.level };
  const displayTurn = node.faded ? node.lastTurn : (node.lastVisibleTurn ?? node.lastTurn);
  const clickable = level.key === 'skill' && typeof onSelectEntry === 'function';
  const nodeClassName = `ts3-tree-node${node.active ? ' active' : ''}${node.faded ? ' ts3-tree-node--faded' : ''}${clickable ? ' ts3-tree-node--clickable' : ''}`;
  const nodeContent = (
    <>
      <span className="ts3-tree-level">{level.label}</span>
      <span className={`ts3-tree-value ts3-tree-value--${level.key}`}>
        {formatLabel(node.value)}
      </span>
      {displayTurn ? <span className="ts3-tree-turn">{`turn ${displayTurn}`}</span> : null}
    </>
  );
  return (
    <li className={`ts3-tree-branch depth-${depth}${node.children.length ? ' ts3-tree-branch--parent' : ''}${node.faded ? ' ts3-tree-branch--faded' : ''}`}>
      {clickable ? (
        <button
          type="button"
          className={nodeClassName}
          onClick={() => onSelectEntry(node.firstIndex)}
          onMouseEnter={() => onHoverEntry && onHoverEntry(node.firstIndex)}
          onMouseLeave={() => onHoverEntry && onHoverEntry(null)}
          onFocus={() => onHoverEntry && onHoverEntry(node.firstIndex)}
          onBlur={() => onHoverEntry && onHoverEntry(null)}
          title={displayTurn ? `View volley at turn ${displayTurn}` : 'View this volley'}
        >
          {nodeContent}
        </button>
      ) : (
        <div className={nodeClassName}>{nodeContent}</div>
      )}
      {node.children.length ? (
        <ol className="ts3-tree-children">
          {node.children.map((child, index) => (
            <TraversalNode
              key={`${child.level}-${child.value}-${child.lastTurn}-${index}`}
              node={child}
              depth={depth + 1}
              onSelectEntry={onSelectEntry}
              onHoverEntry={onHoverEntry}
            />
          ))}
        </ol>
      ) : null}
    </li>
  );
}

function Ts3TraversalPanel({ series = [], visibleCount = 0, collapsed, onToggle, onSelectEntry, onHoverEntry }) {
  const bodyRef = React.useRef(null);
  const clampedVisibleCount = Math.max(0, Math.min(series.length, visibleCount));
  const activeIndex = Math.max(0, clampedVisibleCount - 1);
  const latest = series[activeIndex] || series[series.length - 1] || null;
  const effectiveVisibleCount = clampedVisibleCount || series.length;
  const tree = buildTraversalTree(series, effectiveVisibleCount);

  React.useEffect(() => {
    if (collapsed || !bodyRef.current) {
      return;
    }

    const body = bodyRef.current;
    const activeNodes = body.querySelectorAll('.ts3-tree-node.active');
    const target = activeNodes[activeNodes.length - 1];
    if (!target) {
      body.scrollTop = body.scrollHeight;
      return;
    }

    const bodyRect = body.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    body.scrollTop += targetRect.top - bodyRect.top - (body.clientHeight - targetRect.height) / 2;
  }, [collapsed, series.length, activeIndex]);

  return (
    <section className="ts3-section" aria-label="TS3 traversal">
      <div className="analytics-header">
        <div className="analytics-header-row">
          <div className="analytics-header-copy">
            <h4 className="analytics-title">TS3 Traversal</h4>
          </div>
          <div className="analytics-header-right">
            {latest ? (
              <div className="ts3-latest">
                <span className="ts3-latest__label">Current</span>
                <strong className="ts3-latest__value">{formatLabel(latest.skill)}</strong>
              </div>
            ) : null}
            <button
              type="button"
              className="analytics-toggle-chevron"
              onClick={onToggle}
              aria-expanded={!collapsed}
              aria-label={collapsed ? 'Expand TS3 traversal' : 'Collapse TS3 traversal'}
              title={collapsed ? 'Expand TS3 traversal' : 'Collapse TS3 traversal'}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                {collapsed ? <polyline points="6 15 12 9 18 15" /> : <polyline points="6 9 12 15 18 9" />}
              </svg>
            </button>
          </div>
        </div>
        {!collapsed && <div className="analytics-header-divider" aria-hidden="true" />}
      </div>

      {!collapsed && (
        <div className="ts3-traversal-body" ref={bodyRef}>
          {tree.length ? (
            <ol className="ts3-tree-list">
              {tree.map((node, index) => (
                <TraversalNode
                  key={`${node.level}-${node.value}-${node.lastTurn}-${index}`}
                  node={node}
                  depth={0}
                  onSelectEntry={onSelectEntry}
                  onHoverEntry={onHoverEntry}
                />
              ))}
            </ol>
          ) : (
            <div className="ts3-empty-state">Strategy-Guided selections will appear here.</div>
          )}
        </div>
      )}
    </section>
  );
}

export default Ts3TraversalPanel;
